import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiClient } from "../../services/api";
import { HelpCircle } from "lucide-react";
import { trackEvent } from "../../utils/matomoAnalytics";
import {
  FuelSelector,
  PostcodeInput,
  AddressSelection,
  DateSelector,
  UsageInput,
} from "./components";
import {
  Loader,
  Header,
  Footer,
  BlueTextSection,
  LoadingScreen,
} from "../common";
import ErrorScreen from "../common/ErrorScreen/ErrorScreen";
import MainContent from "../MainContent";
import {
  calculateBaseValues,
  calculateAnnualConsumption,
  getMpanDetails,
  buildElectricityQuoteData,
  buildGasQuoteData,
  buildFormData,
  validateMeterSelection,
  getQuoteEndpoint,
} from "./helpers/quoteFormHelpers";
import styles from "./FuelQuoteForm.module.css";

const FuelQuoteForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedFuel, setSelectedFuel] = useState("electricity");
  const [postcode, setPostcode] = useState("");
  const [showManualAddress, setShowManualAddress] = useState(false);
  const [addressData, setAddressData] = useState(null);
  const [manualAddress, setManualAddress] = useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    postcode: "",
    meterNumber: "",
    meterType: "",
    mpanCore: "",
  });
  useEffect(() => {
    // Only reset when fuel changes and not coming from quotes screen
    if (!location.state?.formData) {
      setShowManualAddress(false);
      setDetectedMeterType("");
    }
  }, [selectedFuel, location.state]);

  const getDefaultDate = () => {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + 21);

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    return {
      day: minDate.getDate().toString(),
      month: monthNames[minDate.getMonth()],
      year: minDate.getFullYear().toString(),
    };
  };

  const [selectedDate, setSelectedDate] = useState(getDefaultDate());
  const [usageType, setUsageType] = useState("kwh");
  const [usageValue, setUsageValue] = useState("");
  const [usagePeriod, setUsagePeriod] = useState("Month");
  const [dayOrPeakValue, setDayOrPeakValue] = useState("");
  const [nightOrOffPeakValue, setNightOrOffPeakValue] = useState("");
  const [mpan, setMpan] = useState("");
  const [mprn, setMprn] = useState("");
  const [showMeterInfo, setShowMeterInfo] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [mpanError, setMpanError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [detectedMeterType, setDetectedMeterType] = useState("");
  const [selectedAddressData, setSelectedAddressData] = useState(null);
  const [singleRateBilling, setSingleRateBilling] = useState(false);
  const [meterType, setMeterType] = useState("");
  const [showError, setShowError] = useState(false);
  const [hasFormData, setHasFormData] = useState(false);
  const usageInputRef = React.useRef();
  const addressSelectionRef = React.useRef();
  const formDataRef = React.useRef(null);
  if (sessionStorage.getItem("navigated") === null) {
    sessionStorage.setItem("navigated", "false");
  }

  useEffect(() => {
    const handleBeforeUnload = () => {
      // Clear everything
      sessionStorage.clear();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [location?.state?.formData]);

  useEffect(() => {
    let formData = location.state?.formData;
    const isPageRefresh = sessionStorage.getItem("navigated") === "false";

    if (isPageRefresh && location.state?.formData) {
      //reset formdata
      sessionStorage.removeItem("navigated");
      formData = null;
    }

    if (formData) {
      setHasFormData(true);
      setSelectedFuel(formData.selectedFuel || "electricity");
      setPostcode(formData.postcode || "");
      setShowManualAddress(formData.showManualAddress || false);
      setManualAddress(
        formData.manualAddress || {
          addressLine1: "",
          addressLine2: "",
          city: "",
          postcode: "",
          meterNumber: "",
          meterType: "",
          mpanCore: "",
        },
      );
      setSelectedDate(formData.selectedDate || getDefaultDate());
      setUsageType(formData.usageType || "kwh");
      setUsageValue(formData.usageValue || "");
      setUsagePeriod(formData.usagePeriod || "Month");
      setDayOrPeakValue(formData.dayOrPeakValue || "");
      setNightOrOffPeakValue(formData.nightOrOffPeakValue || "");
      setMpan(formData.mpan || "");
      setMprn(formData.mprn || "");
      setSelectedAddress(formData.selectedAddress || "");
      setDetectedMeterType(formData.detectedMeterType || "");
      setSelectedAddressData(formData.selectedAddressData || null);
      setSingleRateBilling(formData.singleRateBilling || false);
      setMeterType(formData.meterType || "");
      setAddressData(formData.addressData || null);
    } else {
      setHasFormData(false);
      //set everything to default
      setPostcode("");
      setShowManualAddress(false);
      setManualAddress({
        addressLine1: "",
        addressLine2: "",
        city: "",
        postcode: "",
        meterNumber: "",
        meterType: "",
        mpanCore: "",
      });
      setSelectedDate(getDefaultDate());
      setUsageType("kwh");
      setUsageValue("");
      setUsagePeriod("Month");
      setDayOrPeakValue("");
      setNightOrOffPeakValue("");
      setMpan("");
      setMprn("");
      setSelectedAddress("");
      setDetectedMeterType("");
      setSelectedAddressData(null);
      setSingleRateBilling(false);
      setMeterType("");
      setAddressData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.state?.formData]);

  const handleManualAddressChange = (field, value) => {
    setManualAddress((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (field === "detectedMeterType") {
      setDetectedMeterType(value);
    }
  };

  const handleDateChange = (field, value) => {
    setSelectedDate((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePostcodeLookup = (addressResults, loading) => {
    setIsLoadingAddresses(loading);
    setAddressData(addressResults);
    if (loading) {
      setSelectedAddress("");
      setMpan("");
      setMprn("");
    }
  };

  const handleAddressSelect = (addressData, address, meterType) => {
    setSelectedAddress(address || "");
    setMpanError("");
    setSelectedAddressData(addressData);
    if (addressData) {
      setMpan(addressData.mpanCore || "");
      setMprn(addressData.mprn || "");
    } else {
      setMpan("");
      setMprn("");
    }
    setDetectedMeterType(meterType || "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Track form submission event in Matomo
    trackEvent("Quote Form", "Submit", selectedFuel, null);

    const currentFormData = buildFormData({
      selectedFuel,
      postcode,
      showManualAddress,
      manualAddress,
      selectedDate,
      usageType,
      usageValue,
      usagePeriod,
      mpan,
      mprn,
      selectedAddress,
      detectedMeterType,
      selectedAddressData,
      singleRateBilling,
      meterType,
      addressData,
      dayOrPeakValue,
      nightOrOffPeakValue,
    });

    formDataRef.current = currentFormData;

    const isUsageValid = usageInputRef.current?.validate() ?? true;
    const isAddressValid = addressSelectionRef.current?.validate() ?? true;

    // Validate MPAN/MPRN field
    const meterValidation = validateMeterSelection(
      showManualAddress,
      selectedAddress,
      selectedFuel,
    );
    setMpanError(meterValidation.error);

    if (!isUsageValid || !isAddressValid || !meterValidation.isValid) {
      return;
    }

    // Capture ref values before async operations
    const capturedDayValue = usageInputRef.current?.dayValue || "";
    const capturedNightValue = usageInputRef.current?.nightValue || "";

    setIsLoading(true);

    try {
      const { baseValue, secondBaseValue } = calculateBaseValues(
        detectedMeterType,
        usageType,
        capturedDayValue,
        capturedNightValue,
        usageValue,
      );

      const { annualConsumption, consumptionNightRate } =
        calculateAnnualConsumption(baseValue, secondBaseValue, usagePeriod);

      const quoteData =
        selectedFuel === "electricity"
          ? buildElectricityQuoteData({
              mpanDetails: getMpanDetails(
                showManualAddress,
                manualAddress,
                mpan,
                selectedAddressData,
              ),
              showManualAddress,
              usageType,
              detectedMeterType,
              singleRateBilling,
              annualConsumption,
              consumptionNightRate,
              selectedDate,
              selectedAddressData,
            })
          : buildGasQuoteData({
              annualConsumption,
              usageType,
              selectedAddressData,
              meterType,
              showManualAddress,
              detectedMeterType,
              mprn,
              postcode,
              selectedDate,
            });

      const endpoint = getQuoteEndpoint(selectedFuel);

      const formData = buildFormData({
        selectedFuel,
        postcode,
        showManualAddress,
        manualAddress,
        selectedDate,
        usageType,
        usageValue,
        usagePeriod,
        mpan,
        mprn,
        selectedAddress,
        detectedMeterType,
        selectedAddressData,
        singleRateBilling,
        meterType,
        addressData,
        dayOrPeakValue: capturedDayValue,
        nightOrOffPeakValue: capturedNightValue,
      });

      const response = await apiClient.post(endpoint, quoteData);

      if (response?.results && response.results.length > 0) {
        navigate("/quotes", {
          state: {
            quotesData: response.results,
            selectedFuel,
            formData,
          },
        });
      } else {
        setShowError(true);
        navigate({ state: { formData } });
      }
    } catch (error) {
      if (error) {
        setShowError(true);
        navigate({ state: currentFormData });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to generate aria-label for meter help icon
  const getMeterHelpAriaLabel = () => {
    const action = showMeterInfo ? "Hide" : "Show";
    const meterType = selectedFuel === "electricity" ? "MPAN" : "MPRN";
    return `${action} ${meterType} help information`;
  };

  const renderMainContent = () => {
    if (showError) {
      return (
        <ErrorScreen
          errorCondition={setShowError}
          formData={formDataRef.current}
        />
      );
    }

    if (isLoading) {
      return <LoadingScreen />;
    }

    return (
      <>
        {/* Blue Text Section */}
        <BlueTextSection />

        {/* Form Section - max 1200px width, responsive on smaller screens */}
        <div className={styles.formContainer}>
          <form onSubmit={handleSubmit} className={styles.fuelQuoteForm}>
            {/* Main Content Box */}
            <div className={styles.mainContentBox}>
              <FuelSelector
                selectedFuel={selectedFuel}
                onFuelChange={setSelectedFuel}
              />
              <PostcodeInput
                postcode={postcode}
                onPostcodeChange={setPostcode}
                onLookup={handlePostcodeLookup}
                selectedFuel={selectedFuel}
                skipAutoLookup={hasFormData}
              />
              <AddressSelection
                ref={addressSelectionRef}
                showManualAddress={showManualAddress}
                onToggleManualAddress={setShowManualAddress}
                manualAddress={manualAddress}
                onManualAddressChange={handleManualAddressChange}
                addressData={addressData}
                onAddressSelect={handleAddressSelect}
                postcode={postcode}
                isLoadingAddresses={isLoadingAddresses}
                selectedFuel={selectedFuel}
                selectedAddress={selectedAddress}
                onLookup={handlePostcodeLookup}
              />
              <DateSelector
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
              />
              {/* MPAN/MPRN field - only show when not using manual address */}
              {!showManualAddress && (
                <div className={styles.largeInputContainer}>
                  <div className={styles.meterLabelContainer}>
                    <label
                      htmlFor="meter-number-input"
                      className={styles.meterLabel}
                    >
                      {selectedFuel === "electricity"
                        ? "Your electricity Meter Point Administration Number (MPAN)"
                        : "Your gas Meter Point Reference Number (MPRN)"}
                    </label>
                    <button
                      type="button"
                      className={styles.helpIcon}
                      onClick={() => setShowMeterInfo(!showMeterInfo)}
                      aria-label={getMeterHelpAriaLabel()}
                      aria-expanded={showMeterInfo}
                      aria-controls="meter-help-content"
                    >
                      <HelpCircle size={20} />
                    </button>
                  </div>
                  <p className={styles.meterDescription}>
                    {selectedFuel === "electricity"
                      ? "Please check the visible digits of this number against your recent electricity statement or bill."
                      : "Please check the visible digits of this number against your recent gas statement or bill."}
                  </p>
                  {showMeterInfo && (
                    <section
                      id="meter-help-content"
                      className={styles.meterInfoBox}
                      aria-label={
                        selectedFuel === "electricity"
                          ? "MPAN help information"
                          : "MPRN help information"
                      }
                    >
                      <p className={styles.meterInfoText}>
                        {selectedFuel === "electricity" ? (
                          <>
                            This is the electricity meter number associated with
                            your business site address and can be found on your
                            bill or statement. If the MPAN below is missing or
                            incorrect, please call us on{" "}
                            <a
                              href="tel:03336660276"
                              className={styles.phoneLink}
                              aria-label="Call ENGIE at 0333 666 0276"
                            >
                              0333 666 0276
                            </a>{" "}
                            to get a quote.
                          </>
                        ) : (
                          <>
                            Also referred to as an MPR, this is the gas meter
                            number associated with your business site address
                            and can be found on your bill or statement. If the
                            MPRN below is missing or incorrect, please call us
                            on{" "}
                            <a
                              href="tel:03336660276"
                              className={styles.phoneLink}
                              aria-label="Call ENGIE at 0333 666 0276"
                            >
                              0333 666 0276
                            </a>{" "}
                            to get a quote.
                          </>
                        )}
                      </p>
                    </section>
                  )}
                  <input
                    id="meter-number-input"
                    type="text"
                    value={selectedFuel === "electricity" ? mpan : mprn}
                    className={styles.largeTextInput}
                    placeholder="-"
                    readOnly
                    aria-describedby={
                      showMeterInfo ? "meter-help-content" : undefined
                    }
                    aria-invalid={!!mpanError}
                  />
                  {mpanError && (
                    <div
                      role="alert"
                      aria-live="polite"
                      className={styles.mpanErrorMessage}
                    >
                      {mpanError}
                    </div>
                  )}
                </div>
              )}

              {/* Electricity Consumption Box */}
              <UsageInput
                ref={usageInputRef}
                usageType={usageType}
                onUsageTypeChange={setUsageType}
                usageValue={usageValue}
                onUsageValueChange={setUsageValue}
                usagePeriod={usagePeriod}
                onUsagePeriodChange={setUsagePeriod}
                selectedFuel={selectedFuel}
                detectedMeterType={detectedMeterType}
                singleRateBilling={singleRateBilling}
                onSingleRateBillingChange={setSingleRateBilling}
                initialDayValue={dayOrPeakValue}
                initialNightValue={nightOrOffPeakValue}
              />
            </div>

            {/* Submit Button */}
            <div className={styles.submitContainer}>
              <button
                id="mtm-home-getyourquote"
                type="submit"
                className={styles.submitButton}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader size="small" color="white" />
                    <span className={styles.loadingText}>Processing...</span>
                  </>
                ) : (
                  "GET YOUR QUOTE"
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <Footer />
      </>
    );
  };

  return (
    <div className={styles.mainContainer}>
      <Header height="220px" showShadow={false} />
      <MainContent>{renderMainContent()}</MainContent>
    </div>
  );
};

export default FuelQuoteForm;
